import { Box, Button, FormControl, Grid, makeStyles, TableContainer } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import clsx from 'clsx';
import React, { useCallback, useMemo, useState } from 'react';
import { useAsync } from 'react-async';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import InputCheckbox from '../../../../../../../components/Inputs/Checkbox';
import LoadingContainer from '../../../../../../../components/LoadingContainer';
import TableGrid, { MainColumns } from '../../../../../../../components/TableGrid';
import { api } from '../../../../../../../core/api';
import { CONSTANTS } from '../../../../../../../core/config/constants';
import { BenchmarkCompanyFilter } from '../../../../../../../generated/model/benchmarkCompanyFilter';
import { CompanySearchRequest } from '../../../../../../../generated/model/companySearchRequest';
import { TabNamesEnum } from '../../../../../../Benchmark/BenchmarkPage/BenchmarkTabs/BenchmarkTabs';
import {
  useBenchmarkDispatch,
  useBenchmarkState,
} from '../../../../../../Benchmark/Context/BenchmarkContext';
import { BenchmarkContextActionTypes } from '../../../../../../Benchmark/Context/BenchmarkContext.types';
import { useCompaniesState } from '../../../../../shared/CompanyContext/CompanyContext';
import { useErrorsDispatch } from '../../../../../shared/ErrorContext/ErrorContext';
import { ErrorContextActionTypes } from '../../../../../shared/ErrorContext/ErrorContext.types';
import { useFilterState } from '../../../../../shared/FinancialDataContext/FinancialDataContext';
import SearchSummaryTable from '../../../../SearchSummaryTable';
import { modifySelectedCompanies } from './sendBenchmark.utils';

export interface Props {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const CreateBenchmarkModal: React.FC<Props> = ({ setOpen }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { activeBenchmark } = useBenchmarkState();
  const { selectedCompanies } = useCompaniesState();
  const { requestFiltersData, historySearch } = useFilterState();
  const dispatchErrors = useErrorsDispatch();
  const benchmarkDispatch = useBenchmarkDispatch();
  const hasSingleConsolidation = requestFiltersData.accountConsolidation?.value?.length === 1;

  const { register, control } = useForm<BenchmarkCompanyFilter>();
  const includeConsolidated = useWatch<boolean>({ control, name: 'includeConsolidated' });
  const includeUnconsolidated = useWatch<boolean>({ control, name: 'includeUnconsolidated' });
  const allowBenchwiseRegeneration = useWatch<boolean>({
    control,
    name: 'allowBenchwiseRegeneration',
  });

  const onReject = (error: Error) => {
    dispatchErrors({
      type: ErrorContextActionTypes.SetError,
      payload: { error },
    });
    return Promise.reject(error);
  };

  const modifiedSelectedCompanies: string[] = useMemo(
    () => modifySelectedCompanies(selectedCompanies),
    [selectedCompanies],
  );

  const fetchRefinementSummary = useCallback(
    () =>
      api.benchmark.fetchRefinementSummary(activeBenchmark?.id || '', {
        selectedCompanies: modifiedSelectedCompanies,
        includeConsolidated: hasSingleConsolidation ? undefined : includeConsolidated,
        includeUnconsolidated: hasSingleConsolidation ? undefined : includeUnconsolidated,
      }),
    [
      activeBenchmark,
      modifiedSelectedCompanies,
      includeConsolidated,
      includeUnconsolidated,
      hasSingleConsolidation,
    ],
  );
  const { data: refinementData, isLoading: refinementLoading } = useAsync({
    promiseFn: fetchRefinementSummary,
    onReject,
  });

  const requestData: CompanySearchRequest = {
    filters: requestFiltersData,
    historySearch,
  };

  const applyRefinement = async () => {
    try {
      if (activeBenchmark?.id) {
        setIsLoading(true);
        await api.benchmark.applyRefinement(activeBenchmark.id, {
          selectedCompanies: modifiedSelectedCompanies,
          includeConsolidated: hasSingleConsolidation ? undefined : includeConsolidated,
          includeUnconsolidated: hasSingleConsolidation ? undefined : includeUnconsolidated,
          allowBenchwiseRegeneration,
        });
        const response = await api.benchmark.fetchBenchmarkDetails(activeBenchmark.id);
        response.refinementSearchId = '';
        if (response) {
          benchmarkDispatch({
            type: BenchmarkContextActionTypes.SetActiveBenchmarkCompanyId,
            payload: '',
          });
          benchmarkDispatch({
            type: BenchmarkContextActionTypes.SetClickedBenchmarkTabName,
            payload: TabNamesEnum.MANUAL,
          });
          benchmarkDispatch({
            type: BenchmarkContextActionTypes.SetActiveBenchmark,
            payload: response,
          });
        }
      }
    } catch (error) {
      dispatchErrors({
        type: ErrorContextActionTypes.SetError,
        payload: { error },
      });
      setIsLoading(false);
    }
  };

  const promiseFn = useCallback(
    () =>
      api.companySearch.consolidationStats({
        companySearchRequest: requestData,
        selectedCompanies,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedCompanies],
  );
  const { data: counts, isLoading: isCountLoading } = useAsync({
    promiseFn,
    onReject: (error) => {
      dispatchErrors({
        type: ErrorContextActionTypes.SetError,
        payload: { error },
      });
      return Promise.reject(error);
    },
  });

  if (isLoading || isCountLoading) {
    return <LoadingContainer />;
  }

  // Helper for benchwise company limit
  const benchwiseCompanyLimitReached =
    (refinementData?.total ?? 0) > CONSTANTS.MAX_DEFAULT_VERIFY_COMPANIES;

  return (
    <>
      <Grid container>
        <Grid item xs={3} className={clsx(classes.gridItem, classes.statsGridItem)}>
          <Box>
            <ul className={classes.statsList}>
              <li>Initial count: {refinementData?.initialCount}</li>
              <li>Added: {refinementData?.added}</li>
              <li>Removed: {refinementData?.removed}</li>
              <li>No change: {refinementData?.noChange}</li>
              <li>Total: {refinementData?.total}</li>
            </ul>
          </Box>
        </Grid>
        <Grid item xs={9} className={clsx(classes.gridItem)}>
          <SearchSummaryTable withCounts={false} />
        </Grid>
        <form>
          {!hasSingleConsolidation &&
            counts &&
            counts.consolidatedCompanies > 0 &&
            counts.unconsolidatedCompanies > 0 && (
              <FormControl>
                <Box color="error.main" className={classes.consolidationWarning}>
                  {!!includeConsolidated === !!includeUnconsolidated &&
                    t('benchmark:sendToBenchmark.multipleConsolidationsWarning')}
                </Box>
                <InputCheckbox
                  label={t('benchmark:sendToBenchmark.includeConsolidated', {
                    count: counts.consolidatedCompanies,
                  })}
                  name="includeConsolidated"
                  defaultValue={false}
                  refReg={register}
                />
                <InputCheckbox
                  label={t('benchmark:sendToBenchmark.includeUnconsolidated', {
                    count: counts.unconsolidatedCompanies,
                  })}
                  name="includeUnconsolidated"
                  defaultValue={false}
                  refReg={register}
                />
              </FormControl>
            )}
          {/* Additional warning and checkbox for CompID [AI] (benchwise) report */}
          <FormControl>
            <Box color="warning.main" className={classes.consolidationWarning}>
              {benchwiseCompanyLimitReached
                ? t(
                    'benchmark:sendToBenchmark.benchwiseCompanyLimitReached',
                    `Only up to ${CONSTANTS.MAX_DEFAULT_VERIFY_COMPANIES} companies can be processes by CompID [AI]`,
                  )
                : t(
                    'benchmark:sendToBenchmark.benchwiseRegenerationWarning',
                    'Refinement might change CompID [AI] report.',
                  )}
            </Box>
            <InputCheckbox
              label={t(
                'benchmark:sendToBenchmark.allowBenchwiseRegeneration',
                'Allow CompID [AI] report regeneration. This will change the report and may take some time.',
              )}
              name="allowBenchwiseRegeneration"
              defaultValue={!benchwiseCompanyLimitReached}
              disabled={benchwiseCompanyLimitReached}
              refReg={register}
            />
          </FormControl>
        </form>
        <Grid item xs={12} className={clsx(classes.gridItem)}>
          <TableContainer component={Paper}>
            <TableGrid
              columns={cols}
              rowSelection={false}
              isShowSideBar={false}
              translationKey={'benchmark:sendToBenchmark.refinementColumns'}
              originalProps={{
                rowData: refinementData?.refinementCompanies,
                rowModelType: 'clientSide',
                containerStyle: { height: '480px' },
                defaultColDef: {
                  resizable: false,
                  flex: 1,
                  menuTabs: [],
                  autoHeight: false,
                  wrapText: false,
                  sortable: false,
                },
              }}
            />
          </TableContainer>
        </Grid>
        <Grid item xs={6} className={clsx(classes.gridItem)}>
          <Button color="primary" variant="outlined" onClick={() => setOpen(false)}>
            {t('benchmark:sendToBenchmark.backToRefinementButton')}
          </Button>
        </Grid>
        <Grid item xs={6} className={clsx(classes.gridItem, classes.updateButtonGridItem)}>
          <Button
            color="primary"
            variant="contained"
            onClick={applyRefinement}
            disabled={refinementLoading}
          >
            {t('benchmark:sendToBenchmark.updateBenchmarkingButton')}
          </Button>
        </Grid>
      </Grid>
    </>
  );
};

const cols: MainColumns[] = [
  { field: 'resolutionStatus', maxWidth: 150 },
  { field: 'name', maxWidth: 630 },
  { field: 'refinementStatus', maxWidth: 120 },
];

const useStyles = makeStyles((theme) => ({
  gridItem: {
    padding: theme.spacing(4),
  },
  statsList: {
    listStyleType: 'none',
  },
  statsGridItem: {
    display: 'flex',
    alignItems: 'center',
  },
  updateButtonGridItem: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  consolidationWarning: {
    paddingTop: theme.spacing(2),
    height: '45px',
  },
}));

export default CreateBenchmarkModal;
